import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api from "@/lib/api";
import Seo from "@/components/Seo";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import GoodiesManager from "@/components/GoodiesManager";
import DiscoveryManager from "@/components/DiscoveryManager";
import ArtistWatch from "@/components/ArtistWatch";
import ModerationManager from "@/components/ModerationManager";
import DirectoryManager from "@/components/DirectoryManager";
import EventsManager from "@/components/EventsManager";
import PartnersManager from "@/components/PartnersManager";
import ProfessionalsManager from "@/components/ProfessionalsManager";
import FeaturingManager from "@/components/FeaturingManager";
import OpportunitiesManager from "@/components/OpportunitiesManager";
import AdminDashboard from "@/components/AdminDashboard";
import AdminAnalytics from "@/components/AdminAnalytics";
import DuplicatesManager from "@/components/DuplicatesManager";
import CampfireManager from "@/components/CampfireManager";
import { formatDateRange } from "@/lib/format";
import { Shield, Check, X, Clock, Truck, ExternalLink, LayoutDashboard, CalendarDays, Music, MessageSquare, Briefcase, Flame, ShoppingBag, Radar } from "lucide-react";

export default function Admin() {
  const { lang, t, tCategory } = useLang();
  const { user, loading } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");
  const isAdmin = !!user && user.role === "admin";

  const { data: pending = [] } = useQuery({ queryKey: ["adminPending"], queryFn: async () => (await api.get("/admin/events/pending")).data, enabled: isAdmin });
  const { data: concertQueue = [] } = useQuery({ queryKey: ["concertQueue"], queryFn: async () => (await api.get("/admin/concert-watch/pending")).data, enabled: isAdmin });
  const { data: discoveries = [] } = useQuery({ queryKey: ["discoveries"], queryFn: async () => (await api.get("/admin/discoveries")).data, enabled: isAdmin });
  const { data: reports = [] } = useQuery({ queryKey: ["adminReports"], queryFn: async () => (await api.get("/admin/reports")).data, enabled: isAdmin });

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/events/${id}/moderate`, { action });
      toast.success(action === "approve" ? t("admin_approve") : t("admin_reject"));
      qc.invalidateQueries({ queryKey: ["adminPending"] });
      qc.invalidateQueries({ queryKey: ["adminStats"] });
      qc.invalidateQueries({ queryKey: ["adminDashboard"] });
    } catch { toast.error("Error"); }
  };

  if (loading) return <div className="py-24 text-center text-stone-400">{t("loading")}</div>;
  if (!isAdmin) return <div className="py-24 text-center text-stone-400">403 — {t("nav_admin")}</div>;

  const TABS = [
    { id: "overview", label: "Vue d'ensemble", icon: LayoutDashboard },
    { id: "events", label: "Événements", icon: CalendarDays, badge: pending.length },
    { id: "discovery", label: "Découverte auto", icon: Radar, badge: discoveries.length },
    { id: "concerts", label: "Concerts", icon: Music, badge: concertQueue.length },
    { id: "community", label: "Communauté", icon: MessageSquare, badge: reports.length },
    { id: "pros", label: "Professionnels", icon: Briefcase },
    { id: "campfire", label: "Campfire", icon: Flame },
    { id: "shop", label: "Boutique", icon: ShoppingBag },
  ];

  return (
    <>
      <Seo title={t("nav_admin")} canonicalPath="/admin" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6 border-b border-amber-900/30 pb-5">
          <Shield className="w-7 h-7 text-amber-500" />
          <h1 className="font-serif text-3xl sm:text-4xl uppercase tracking-tight text-amber-100">{t("admin_title")}</h1>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          <aside className="lg:w-60 shrink-0">
            <nav className="lg:sticky lg:top-24 flex lg:flex-col gap-1 overflow-x-auto pb-2 lg:pb-0" data-testid="admin-sidebar">
              {TABS.map((tb) => {
                const Icon = tb.icon;
                const active = tab === tb.id;
                return (
                  <button key={tb.id} data-testid={`admin-tab-${tb.id}`} onClick={() => setTab(tb.id)}
                    className={`group flex items-center gap-3 px-3.5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors shrink-0 lg:w-full ${active ? "bg-amber-600 text-stone-950" : "text-stone-300 hover:bg-[#181A20] hover:text-amber-100"}`}>
                    <Icon className={`w-4 h-4 shrink-0 ${active ? "text-stone-950" : "text-amber-500"}`} />
                    <span className="flex-1 text-left">{tb.label}</span>
                    {tb.badge > 0 && (
                      <span data-testid={`admin-tab-badge-${tb.id}`} className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${active ? "bg-stone-950 text-amber-400" : "bg-amber-600 text-stone-950"}`}>{tb.badge}</span>
                    )}
                  </button>
                );
              })}
            </nav>
          </aside>

          <main className="flex-1 min-w-0" data-testid="admin-content">
            {tab === "overview" && (
              <div data-testid="admin-panel-overview">
                <AdminDashboard />
                <AdminAnalytics />
              </div>
            )}

            {tab === "events" && (
              <div data-testid="admin-panel-events">
                <h2 className="font-serif text-2xl text-stone-100 mb-6 flex items-center gap-2"><Clock className="w-5 h-5 text-amber-500" /> {t("admin_pending")} ({pending.length})</h2>
                {pending.length === 0 ? (
                  <div className="text-center py-16 text-stone-400 border border-dashed border-[#272A34] rounded-lg" data-testid="admin-no-pending">{t("admin_none")}</div>
                ) : (
                  <div className="space-y-4">
                    {pending.map((e) => (
                      <div key={e.id} data-testid={`pending-event-${e.id}`} className="flex flex-col sm:flex-row gap-4 bg-[#121418] border border-[#272A34] rounded-lg p-4">
                        {e.image && <img src={e.image} alt="" className="w-full sm:w-40 h-28 object-cover rounded-md" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-mono text-[11px] uppercase tracking-wider text-amber-500 mb-1">{tCategory(e.category)}</div>
                          <h3 className="font-serif text-lg text-stone-100 mb-1">{e.title}</h3>
                          <p className="text-sm text-stone-400 line-clamp-2 mb-2">{e.description}</p>
                          <div className="text-xs text-stone-500 font-mono">{e.city}, {e.country} · {formatDateRange(e.start_date, e.end_date, lang)} · {e.organizer_name}</div>
                        </div>
                        <div className="flex sm:flex-col gap-2 shrink-0">
                          <button data-testid={`approve-btn-${e.id}`} onClick={() => moderate(e.id, "approve")} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-700 hover:bg-emerald-600 text-white px-4 py-2 rounded-md text-sm font-medium"><Check className="w-4 h-4" /> {t("admin_approve")}</button>
                          <button data-testid={`reject-btn-${e.id}`} onClick={() => moderate(e.id, "reject")} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-red-900/70 hover:bg-red-800 text-white px-4 py-2 rounded-md text-sm font-medium"><X className="w-4 h-4" /> {t("admin_reject")}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                <EventsManager />
                <DuplicatesManager />
              </div>
            )}

            {tab === "discovery" && (
              <div data-testid="admin-panel-discovery">
                <DiscoveryManager />
              </div>
            )}

            {tab === "concerts" && (
              <div data-testid="admin-panel-concerts">
                <ArtistWatch />
              </div>
            )}

            {tab === "community" && (
              <div data-testid="admin-panel-community">
                <ModerationManager />
              </div>
            )}

            {tab === "pros" && (
              <div data-testid="admin-panel-pros">
                <ProfessionalsManager />
                <FeaturingManager />
                <OpportunitiesManager />
                <PartnersManager />
                <DirectoryManager />
              </div>
            )}

            {tab === "campfire" && (
              <div data-testid="admin-panel-campfire">
                <CampfireManager />
              </div>
            )}

            {tab === "shop" && (
              <div data-testid="admin-panel-shop">
                <GoodiesManager />
              </div>
            )}
          </main>
        </div>
      </div>
    </>
  );
}
